/**
 * SeasonEpisode entity class
 */
import moment from 'moment'
import { timeStringToDate, formatDateByTimezone } from 'app/helpers/date'
import { showLocalizationPairs, metadataMapToLocalizations, localizationsToMetadataMap } from 'app/helpers/localization'
import { skipSegmentFields, skipSegmentMirrorFields, prepareSkipSegment, serializeSkipSegment } from 'app/helpers/skipIntroCredits'

// Episode localized metadata: the localized title is stored under `name` (the
// directive's default key) to match the backend ShowLocalizationMetadataDto;
// verticalImage/horizontalImage map to portraitImageUrl / backgroundImageUrl.
const EPISODE_LOCALIZATION_PAIRS = showLocalizationPairs('portraitImageUrl', 'backgroundImageUrl')

/**
 * SeasonEpisodeFactory
 * returns SeasonEpisode entity class
 *
 * @class SeasonEpisodeFactory
 * @memberof mwApp
 * @see mwApp
 * @return {Function} {@link models.SeasonEpisode} class
 *
 * @ngdoc factory
 * @name mwApp.factory:SeasonEpisodeFactory
 */
/* @ngInject */
export default function SeasonEpisodeFactory ($http, Entity, Episode, ShowSet, Network, FileUploadMixin, UrlPolicy, authSvc, alertLiteSvc) {
  /* === SeasonEpisode class === */

  /**
   * SeasonEpisode model
   *
   * @class SeasonEpisode
   * @classdesc SeasonEpisode model
   * @memberof models
   * @see models
   * @extends models.Entity
   * @mixes models.m2mMixin
   * @mixes models.onOffMixin
   * @return {models.SeasonEpisode} SeasonEpisode entity
   */
  function SeasonEpisode () {
    var obj = Entity.apply(this, arguments)
    if (obj) {
      return obj
    }
  }
  SeasonEpisode.prototype = Object.create(Episode.prototype)
  SeasonEpisode.prototype.constructor = SeasonEpisode

  /* === SeasonEpisode instance methods & fields === */

  Object.assign(SeasonEpisode.prototype, {

    /**
     * instance fields
     *
     * @memberof models.SeasonEpisode#
     * @type {Object}
     */
    _fields: {
      id: Number,
      title: String,
      description: String,
      directors: String,
      stars: String,
      releasedTime: String,
      resolution: 'HD',
      dolbyAtmosAvailable: Boolean,
      offlinePlaybackEnabled: Boolean,
      offlinePlaybackExpirationDays: Number,
      length: String,
      enabled: Boolean,
      sortOrder: Number,
      displayNumber: String,
      views: Number,
      portraitImageUrl: String,
      backgroundImageUrl: String,
      localizationMetadata: {}, // map<langCode, {name,description,directors,stars,horizontalImage,verticalImage}>
      newEpisodePosition: String,
      urls: {
        STB: 'MediaUrlDTO',
        PC: 'MediaUrlDTO',
        iOS: 'MediaUrlDTO',
        Android: 'MediaUrlDTO',
        SamsungTV: 'MediaUrlDTO',
        Others: 'MediaUrlDTO',
        WEB: 'MediaUrlDTO'
      },
      tvShow: {
        id: Number
      },
      season: {
        id: Number
      },
      publishTime: String,
      unpublishTime: String,
      contentSets: ['ShowSet'],
      assignedSubscriptions: ['Subscription'],
      defaultStream: 'DefaultStream',
      policedStreams: null, // 'PolicedStreams'
      streamPolicy: 'UrlPolicy',
      streamBindings: ['StreamBinding'],
      skipIntro: skipSegmentFields(),
      skipCredits: skipSegmentFields(),

      /* local variables */
      $skipIntro: skipSegmentMirrorFields(),
      $skipCredits: skipSegmentMirrorFields(),
      $scheduledPublishing: Boolean,
      $fromDate: Date,
      $fromTime: Date,
      $toDate: Date,
      $toTime: Date,
      $toDateUnlimited: true,
      $itemType: 'episode',
      $itemTypeForContentSet: 'show',
      $contentSetType: 'showSet',
      $contentSetName: 'Show Set',
      $tvShow: 'Show'
    },

    /**
     * url template
     *
     * @memberof models.SeasonEpisode#
     * @type {String}
     */
    _urlTemplate: 'api/tvshows/{{::tvShow.id}}/seasons/{{::season.id}}/episodes/{{::id}}',

    /**
     * public url template
     *
     * @memberof models.SeasonEpisode#
     * @type {String}
     */
    _publicUrlTemplate: 'shows/{{::tvShow.id}}/seasons/{{::season.id}}/episodes/{{::id}}',

    /**
     * Load entities for SmartTable <br>
     * SmartTable state looks like Entity.loadSmart
     *
     * @memberof models.Episode
     * @param {Object} tableState - SmartTable state
     * @return {Promise} promise
     */
    _updateSmart: function (tableState) {
      return SeasonEpisode.loadSmart(tableState, { tvShow: { id: this.tvShow.id }, season: { id: this.season.id } })
    },

    /**
     * Prepare channel
     *
     * @memberof models.SeasonEpisode#
     * @override
     */
    _prepare: function () {
      UrlPolicy.prepareToLoadContentItem(this)

      if ((!authSvc.hasEnabledFlag('WEB_PLATFORM_ENABLED')) && this.urls) {
        delete this.urls.WEB
      }

      /* Note: set default values. */
      this.$scheduledPublishing = !!this.publishTime
      this.$toDateUnlimited = !this.unpublishTime

      this.releasedTime = this.releasedTime ? moment(this.releasedTime).toDate() : moment().toDate()
      this.$fromDate = this.publishTime ? formatDateByTimezone(this.publishTime, false).toDate() : moment().toDate()
      this.$toDate = this.unpublishTime ? formatDateByTimezone(this.unpublishTime, false).toDate() : this.$fromDate
      this.$fromTime = timeStringToDate(this.publishTime ? moment.utc(this.publishTime).format('HH:mm') : null, 'startOfDay')
      this.$toTime = timeStringToDate(this.unpublishTime ? moment.utc(this.unpublishTime).format('HH:mm') : null, 'endOfDay')

      // Build the directive-friendly localizations array from the backend map.
      this.localizations = metadataMapToLocalizations(this.localizationMetadata, EPISODE_LOCALIZATION_PAIRS)

      // Skip Intro / Skip Credits: build the uib-timepicker Date mirrors.
      this.$skipIntro = prepareSkipSegment(this.skipIntro)
      this.$skipCredits = prepareSkipSegment(this.skipCredits)
    },
    /**
     * Get underlying object
     *
     * @memberof models.SeasonEpisode#
     * @return {Object} new underlying object as plain object
     * @override
     */
    _object: function () {
      UrlPolicy.prepareToSaveContentItem(this)

      this.releasedTime = moment(this.releasedTime).startOf('day').format('YYYY-MM-DDTHH:mm:ss[Z]')

      const fromDate = this.$fromDate.setHours(this.$fromTime.getHours(), this.$fromTime.getMinutes(), 0)
      const toDate = this.$toDate.setHours(this.$toTime.getHours(), this.$toTime.getMinutes(), 0)
      this.publishTime = formatDateByTimezone(fromDate).format('YYYY-MM-DDTHH:mm:ss[Z]')
      this.unpublishTime = formatDateByTimezone(toDate).format('YYYY-MM-DDTHH:mm:ss[Z]')

      if (this.$toDateUnlimited) {
        this.unpublishTime = null
      }

      if (!this.$scheduledPublishing) {
        this.publishTime = null
        this.unpublishTime = null
      }

      // Serialize the localizations array back into the backend map shape.
      this.localizationMetadata = localizationsToMetadataMap(this.localizations, EPISODE_LOCALIZATION_PAIRS)

      // Skip Intro / Skip Credits: serialize the Date mirrors back to "HH:MM:SS"
      // (disabled → {enabled:false, …:null} so disabling actually persists).
      this.skipIntro = serializeSkipSegment(this.$skipIntro)
      this.skipCredits = serializeSkipSegment(this.$skipCredits)

      // delete null urls
      if (this.urls) {
        this.urls =
          Object.keys(this.urls)
            .reduce(
              (o, platform) => {
                if (this.urls[platform] != null) {
                  o[platform] = this.urls[platform]
                }
                return o
              },
              Object.create(null)
            )
      }

      return Entity.prototype._object.apply(this, arguments)
    },

    /**
     * Get image URL for given size
     *
     * @memberof models.SeasonEpisode#
     * @param  {String} fieldName
     * @param  {String} size
     * @return {String}
     */
    _urlFor: function (fieldName, size) {
      return this[fieldName]
        ? this[fieldName].indexOf('{{size}}') !== -1
          ? this[fieldName].replace('{{size}}', size) + '?lastUpload=' + this.$lastUpload
          : this[fieldName] + '?resize=' + size + '&lastUpload=' + this.$lastUpload
        : ''
    }

  }, FileUploadMixin)

  /* === SeasonEpisode static methods & fields === */

  /**
   * Load Content sets
   *
   * @returns {*}
   * @param show
   * @param id
   */
  SeasonEpisode.loadAvailableContentSets = function (show, id) {
    return $http.get(`/nora/api/tvshows/${show.id}/episodes/${id}/extra/content-sets`)
      .data()
      .then(data => data.payload)
      .catch(rejection => alertLiteSvc.alertError(rejection))
  }

  // copy all static methods from Entity class
  Object.assign(SeasonEpisode, Entity)
  SeasonEpisode._name = 'SeasonEpisode'
  SeasonEpisode._publicName = 'Episode'
  SeasonEpisode.init()

  // permissions
  SeasonEpisode.access = {
    view: 'viewVodSeries',
    create: 'createVodSeries',
    edit: 'editVodSeries',
    delete: 'deleteVodSeries'
  }

  // extra
  SeasonEpisode.extra = {
    drmProfiles: ['DRMProfile']
  }

  return SeasonEpisode
}
